import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button } from "@mui/material";

import { useNotify, useUtilityBillById } from "hooks";
import { getFromLocalStorage, setToLocalStorage } from "utils";
import { BillModal, Dialog, Drawer, Loading } from "components";
import {
  useBillEntriesNavigation,
  useDocumentUploadNavigation,
  useFetchNextBillEntriesPage,
  useFetchNextDocUploadPage,
  useFetchPrevBillEntriesPage,
  useFetchPrevDocUploadPage,
} from "recoils";

import { UTILITY_BILL_PAGE } from "../consts";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "./utility-bill-allDataEntries-view/consts";
import { buildNavigationContext } from "./helpers";
import { NavigationGuardContext } from "./use-navigation-guard";
import "./css/style.css"

export const UtilityBill: React.FunctionComponent = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeTab: "document" | "entry" = pathname.endsWith(
    `/${UTILITY_BILL_ALL_DATAENTRIES_VIEW.PATH}`,
  ) ? "document" : "entry";

  const { billId } = useParams<{ [UTILITY_BILL_PAGE.KEY]: string }>();

  const { data: utilityBill, isLoading, isError } = useUtilityBillById(billId || "");

  const [isOpen, setIsOpen] = useState(!!billId);
  const hasHandledError = useRef(false);

  const getType = getFromLocalStorage("type") || "";

  const onClose = useCallback(() => {
    let getURL: string = "";
    if (getType.includes("Upload")) {
      getURL = getFromLocalStorage("Upload_URL") || "";
    }
    if (getType.includes("Emissions")) {
      getURL = getFromLocalStorage("Emission_URL") || "";
    }
    if (getType.includes("Summary")) {
      getURL = getFromLocalStorage("Summary_URL") || "";
    }

    navigate(getURL);
    localStorage.removeItem("type");
    localStorage.removeItem("Upload_URL");

    setIsOpen((state) => !state);
  }, [navigate, getType]);

  useEffect(() => {
    if (utilityBill?.id) {
      setToLocalStorage("billEntriesIdSelected", utilityBill.id);
    }
  }, [utilityBill?.id]);

  useEffect(() => {
    if (hasHandledError.current) {
      return;
    }

    if (!billId || isError) {
      hasHandledError.current = true;
      notify.error("Invalid utility bill ID. Please try again.");
      onClose();
    }
  }, [billId, isError, notify, onClose]);

  const isDirtyRef = useRef(false);
  const resetRef = useRef<(() => void) | null>(null);
  const [pendingNavUrl, setPendingNavUrl] = useState<string | null>(null);

  // Keep stale data visible during navigation to avoid a blank flash between bills.
  const prevBillRef = useRef(utilityBill);
  if (utilityBill) prevBillRef.current = utilityBill;
  const displayBill = utilityBill ?? prevBillRef.current;

  const billEntriesNav = useBillEntriesNavigation();
  const docUploadNav = useDocumentUploadNavigation();
  const fetchNextBillEntriesPage = useFetchNextBillEntriesPage();
  const fetchNextDocUploadPage = useFetchNextDocUploadPage();
  const fetchPrevBillEntriesPage = useFetchPrevBillEntriesPage();
  const fetchPrevDocUploadPage = useFetchPrevDocUploadPage();

  const navigationContext = useMemo(
    () =>
      billId
        ? buildNavigationContext(
            billId,
            activeTab,
            billEntriesNav,
            docUploadNav,
            fetchNextBillEntriesPage,
            fetchNextDocUploadPage,
            fetchPrevBillEntriesPage,
            fetchPrevDocUploadPage,
          )
        : undefined,
    [billId, activeTab, billEntriesNav, docUploadNav, fetchNextBillEntriesPage, fetchNextDocUploadPage, fetchPrevBillEntriesPage, fetchPrevDocUploadPage],
  );

  const doNavigate = useCallback((id: string) => {
    if (activeTab === "document") {
      navigate(UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(id));
    } else {
      navigate(UTILITY_BILL_PAGE.ABSOLUTE_PATH(id));
    }
  }, [navigate, activeTab]);

  const handleNavigate = useCallback((id: string) => {
    if (activeTab === "entry" && isDirtyRef.current) {
      const url = UTILITY_BILL_PAGE.ABSOLUTE_PATH(id);
      setPendingNavUrl(url);
      return;
    }
    doNavigate(id);
  }, [doNavigate, activeTab]);

  const handleLeave = useCallback(() => {
    resetRef.current?.();
    const url = pendingNavUrl!;
    setPendingNavUrl(null);
    navigate(url);
  }, [navigate, pendingNavUrl]);

  const handleStay = useCallback(() => setPendingNavUrl(null), []);

  const guardContextValue = useMemo(
    () => ({ isDirtyRef, resetRef }),
    // Refs are stable objects — this memo only needs to run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <NavigationGuardContext.Provider value={guardContextValue}>
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      zIndex={1300}
      customPaperProps={{ width: "100%", height: "auto", overflow: "hidden" }}
      expandedWidth='100%'
    >
      {displayBill ? (
        <BillModal
          document={displayBill}
          isLoading={isLoading}
          onClose={onClose}
          billId={billId as string}
          navigationContext={navigationContext}
          activeTab={activeTab}
          onNavigate={handleNavigate}
        />
      ) : (
        <Loading />
      )}
    </Drawer>
    <Dialog
      isOpen={pendingNavUrl !== null}
      title="Unsaved changes"
      onClose={handleStay}
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleStay}>
            Stay
          </Button>
          <Button variant="contained" color="error" onClick={handleLeave}>
            Leave without saving
          </Button>
        </Box>
      }
    >
      You have unsaved changes. Leave without saving?
    </Dialog>
    </NavigationGuardContext.Provider>
  );
};
